import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Route, Routes, useLocation } from "react-router";
import "./App.css";
import { AnimatePresence } from "motion/react";
import { useModal } from "@/hooks/useModal";
import { createPortal } from "react-dom";
import { ToastContainer } from "react-toastify";
import { HomePage } from "./pages/Home";

const NotFoundPage = lazy(() =>
    import("@/pages/404").then(({ NotFoundPage }) => ({
        default: NotFoundPage,
    }))
);
const AttractionPage = lazy(() =>
    import("@/pages/Attraction").then(({ AttractionPage }) => ({
        default: AttractionPage,
    }))
);
const AttractionsPage = lazy(() =>
    import("@/pages/Attractions").then(({ AttractionsPage }) => ({
        default: AttractionsPage,
    }))
);
const CheckoutPage = lazy(() =>
    import("@/pages/Checkout").then(({ CheckoutPage }) => ({
        default: CheckoutPage,
    }))
);
const ContactPage = lazy(() => import("@/pages/Contact"));
const CorporateEventPage = lazy(() =>
    import("@/pages/CorporateEvents").then(({ CorporateEventPage }) => ({
        default: CorporateEventPage,
    }))
);
const EventPage = lazy(() =>
    import("@/pages/Event").then(({ EventPage }) => ({
        default: EventPage,
    }))
);
const ExcursaoPage = lazy(() =>
    import("@/pages/Excursao").then(({ ExcursaoPage }) => ({
        default: ExcursaoPage,
    }))
);
const FaqRoute = lazy(async () => {
    const [{ FaqPage }, { mainFaqData }] = await Promise.all([
        import("@/pages/Faq"),
        import("@/pages/Faq/data"),
    ]);

    return {
        default: function FaqRoute() {
            return <FaqPage faqData={mainFaqData} />;
        },
    };
});
const GroupsPage = lazy(() =>
    import("@/pages/Groups").then(({ GroupsPage }) => ({
        default: GroupsPage,
    }))
);
const PrivacyPage = lazy(() =>
    import("@/pages/Privacy").then(({ PrivacyPage }) => ({
        default: PrivacyPage,
    }))
);
const WifiPrivacyRoute = lazy(async () => {
    const [{ PrivacyPage }, { WifiPrivacyFullText }] = await Promise.all([
        import("@/pages/Privacy"),
        import("@/pages/WifiPrivacy/wifiPrivacyFullText"),
    ]);

    return {
        default: function WifiPrivacyRoute() {
            return <PrivacyPage content={<WifiPrivacyFullText />} />;
        },
    };
});
const RulesPage = lazy(() =>
    import("@/pages/Rules").then(({ RulesPage }) => ({
        default: RulesPage,
    }))
);
const SalePage = lazy(() =>
    import("@/pages/Sale").then(({ SalePage }) => ({
        default: SalePage,
    }))
);
const SocialNetworks = lazy(() =>
    import("@/pages/SocialNetworks").then(({ SocialNetworks }) => ({
        default: SocialNetworks,
    }))
);
const StoryPage = lazy(() =>
    import("@/pages/Story").then(({ StoryPage }) => ({
        default: StoryPage,
    }))
);
const TermsPage = lazy(() =>
    import("@/pages/Terms").then(({ TermsPage }) => ({
        default: TermsPage,
    }))
);
const QuizPageLazyQuestions = lazy(async () => {
    const module = await import("@/pages/Quiz");
    return { default: module.QuizPage };
});
const QuizInitialPageLazy = lazy(async () => {
    const module = await import("@/pages/Quiz/InitialPage");
    return { default: module.InitialPage };
});
const TicketPage = lazy(() =>
    import("./pages/Tickets").then(({ TicketPage }) => ({
        default: TicketPage,
    }))
);
const TicketPolicyPage = lazy(() =>
    import("@/pages/TicketPolicy").then(({ TicketPolicyPage }) => ({
        default: TicketPolicyPage,
    }))
);
const VoucherPage = lazy(() =>
    import("@/pages/Voucher").then(({ VoucherPage }) => ({
        default: VoucherPage,
    }))
);
const WorkPage = lazy(() =>
    import("@/pages/Work").then(({ WorkPage }) => ({
        default: WorkPage,
    }))
);

const preloadImportantRoutes = () => {
    void Promise.allSettled([
        import("@/pages/Attractions"),
        import("@/pages/Event"),
        import("@/pages/Groups"),
        import("@/pages/Contact"),
        import("./pages/Tickets"),
    ]);
};

const scheduleImportantRoutesPreload = () => {
    if ("requestIdleCallback" in window) {
        window.requestIdleCallback(preloadImportantRoutes, { timeout: 4000 });
        return;
    }

    window.setTimeout(preloadImportantRoutes, 2500);
};

const RouteFallback = () => (
    <div
        className="route-fallback"
        role="status"
        aria-label="Carregando pagina"
    >
        <div className="route-fallback__spinner" />
    </div>
);

const MyRoutes = () => {
    const location = useLocation();

    useEffect(() => {
        // Scroll suave customizado para o topo
        const root = document.getElementById('root');
        if (root) root.scrollTop = 0;
        
        const SCROLL_DURATION = 1000; // Duração em ms 
        const startTime = performance.now();
        const startScroll = window.scrollY;
        
        const scrollToTop = (currentTime: number) => {
            const elapsed = currentTime - startTime;
            const progress = Math.min(elapsed / SCROLL_DURATION, 1);
            
            // Easing: easeOutCubic para suavidade
            const easeProgress = 1 - Math.pow(1 - progress, 3);
            
            window.scrollTo(0, startScroll * (1 - easeProgress));
            
            if (progress < 1) {
                requestAnimationFrame(scrollToTop);
            }
        };
        
        if (startScroll > 0) {
            requestAnimationFrame(scrollToTop);
        }
    }, [location.pathname]);

    return (
        <AnimatePresence initial={false}>
            <Suspense fallback={<RouteFallback />}>
                <Routes key={location.pathname} location={location}>
                    <Route path="*" element={<NotFoundPage />} />
                    <Route path="/" element={<HomePage />} />
                    <Route path="/historia" element={<StoryPage />} />
                    <Route path="/atracoes" element={<AttractionsPage />} />
                    <Route path="/festas" element={<EventPage />} />
                    <Route
                        path="/eventos-corporativos"
                        element={<CorporateEventPage />}
                    />
                    <Route path="/grupos" element={<GroupsPage />} />
                    <Route path="/escolas" element={<ExcursaoPage />} />
                    <Route path="/faq" element={<FaqRoute />} />
                    <Route path="/contato" element={<ContactPage />} />
                    <Route path="/trabalhe-conosco" element={<WorkPage />} />
                    <Route path="/promocao" element={<SalePage />} />
                    <Route path="/atracao/:id" element={<AttractionPage />} />
                    <Route
                        path="/politica-de-privacidade"
                        element={<PrivacyPage />}
                    />
                    <Route
                        path="/politica-de-privacidade-wifi"
                        element={<WifiPrivacyRoute />}
                    />
                    <Route
                        path="/termos-e-condicoes"
                        element={<TermsPage />}
                    />
                    <Route
                        path="/politica-compra-ingressos"
                        element={<TicketPolicyPage />}
                    />
                    <Route
                        path="/regras-e-procedimentos"
                        element={<RulesPage />}
                    />
                    <Route
                        path="/checkout/:redirectId?/:locatorCode?/:orderId?"
                        element={<CheckoutPage />}
                    />
                    <Route
                        path="/redes-sociais"
                        element={<SocialNetworks />}
                    ></Route>
                    <Route
                        path="/voucher/:voucherId/:autoPrint?"
                        element={<VoucherPage />}
                    ></Route>
                    <Route path="/ingressos" element={<TicketPage />}></Route>
                    <Route
                        path="/quiz/questions"
                        element={<QuizPageLazyQuestions />}
                    />
                    <Route
                        path="/quiz/result"
                        element={
                            <QuizPageLazyQuestions renderResults={true} />
                        }
                    />
                    <Route path="/quiz" element={<QuizInitialPageLazy />} />
                </Routes>
            </Suspense>
        </AnimatePresence>
    );
};
const queryClient = new QueryClient();

function App() {
    const modalStore = useModal();

    useEffect(() => {
        const query = (window.location.search.match(
            new RegExp("[?&]" + "font" + "=([^&]+)")
        ) || [undefined, null])[1];
        if (!query) return;
        document.documentElement.style.setProperty("--global-font-size", query);
        return () => {
            document.documentElement.style.removeProperty("--global-font-size");
        };
    }, []);

    useEffect(() => {
        scheduleImportantRoutesPreload();
    }, []);

    return (
        <QueryClientProvider client={queryClient}>
            <BrowserRouter>
                <MyRoutes />
            </BrowserRouter>

            {createPortal(
                <AnimatePresence>
                    {modalStore.page ? modalStore.page.children : null}
                </AnimatePresence>,
                document.getElementById("root")!
            )}
            <ToastContainer theme="light" closeOnClick={true} />
        </QueryClientProvider>
    );
}

export default App;
